'use client';

import { useSyncExternalStore } from 'react';
import { readLatestLandingJob } from './latestLandingRead.mjs';
import { landingBuildVersion } from './clientVersion';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
const storageKey = (() => {
  try { return `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`; }
  catch { return null; }
})();

let account = null;
let authClient;
let stopWatching;
const readEventTimes = new Map();
const listeners = new Set();

// Presentation only. API permissions continue to be checked by the app/backend.
// Never keep tokens or send account identifiers to landing analytics.
function publish(user, isPremium) {
  const guest = user?.is_anonymous === true;
  const previousPremium = account?.id === user?.id && account?.kind === 'registered' ? account.isPremium : null;
  const next = user?.id ? {
    id: user.id,
    kind: guest ? 'guest' : 'registered',
    email: guest ? '' : (typeof user.email === 'string' ? user.email.trim() : ''),
    isPremium: guest ? false : typeof isPremium === 'boolean' ? isPremium : previousPremium,
  } : null;
  if (account?.id === next?.id && account?.kind === next?.kind && account?.email === next?.email && account?.isPremium === next?.isPremium) return;
  account = next;
  listeners.forEach(listener => listener());
}

function getAuthClient() {
  if (!authClient) {
    authClient = import('@supabase/supabase-js').then(({ createClient }) => createClient(supabaseUrl, supabaseKey, {
      auth: { autoRefreshToken: false, detectSessionInUrl: false },
    })).catch(error => { authClient = null; throw error; });
  }
  return authClient;
}

function watch() {
  if (!storageKey || !supabaseKey) return () => {};
  let disposed = false;
  let revision = 0;
  let timer;
  const refresh = async () => {
    const current = ++revision;
    let session;
    try { session = JSON.parse(window.localStorage.getItem(storageKey) || 'null'); }
    catch { publish(null); return; }
    if (!session?.access_token || !session?.refresh_token || !session?.user?.id) {
      publish(null);
      return;
    }
    publish(session.user);
    try {
      // New visitors never download the auth SDK or make an auth request.
      // Existing sessions are checked and expired access tokens can refresh.
      const client = await getAuthClient();
      if (disposed || current !== revision) return;
      const { data, error } = await client.auth.getUser();
      if (disposed || current !== revision) return;
      if (!error) {
        publish(data.user);
        if (data.user && !data.user.is_anonymous) {
          // Match the app's authenticated subscription check. Never infer a
          // paid plan from editable user metadata or send it to analytics.
          const { data: premium, error: planError } = await client.rpc('check_premium_status');
          if (disposed || current !== revision) return;
          if (!planError && typeof premium === 'boolean') publish(data.user, premium);
        }
      }
      else if (error.status === 401 || error.status === 403 || !window.localStorage.getItem(storageKey)) publish(null);
      // A temporary network error must not make a returning user appear logged out.
    } catch {}
  };
  const schedule = () => { clearTimeout(timer); timer = setTimeout(refresh, 0); };
  const storage = event => {
    // Supabase in the workspace iframe/another tab writes the same origin's
    // storage. This also covers guest upgrades, token refresh and sign-out.
    if (event.key === storageKey || event.key === null) schedule();
  };
  const visible = () => { if (document.visibilityState === 'visible') schedule(); };
  window.addEventListener('storage', storage);
  window.addEventListener('focus', schedule);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', visible);
  refresh();
  return () => {
    disposed = true;
    revision += 1;
    clearTimeout(timer);
    window.removeEventListener('storage', storage);
    window.removeEventListener('focus', schedule);
    window.removeEventListener('pageshow', schedule);
    document.removeEventListener('visibilitychange', visible);
  };
}

function subscribe(listener) {
  listeners.add(listener);
  if (!stopWatching) stopWatching = watch();
  return () => {
    listeners.delete(listener);
    if (!listeners.size) { stopWatching?.(); stopWatching = null; }
  };
}

const getSnapshot = () => account;
const getServerSnapshot = () => null;

export function landingAccountType(account) {
  return ['guest', 'registered'].includes(account?.kind) ? account.kind : 'visitor';
}

export function useLandingAccount() {
  // All static pages and the first hydration render remain identical for SEO.
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export async function fetchLatestLandingJob(userId, signal) {
  if (!account || account.id !== userId) return null;
  const client = await getAuthClient();
  const { data: { session } } = await client.auth.getSession();
  if (signal?.aborted || !session?.access_token || session.user?.id !== userId || account?.id !== userId) return null;
  const base = (process.env.NEXT_PUBLIC_API_URL || 'https://api.instanttranscriber.com').replace(/\/$/, '');
  const version = landingBuildVersion();
  const release = /^[a-f0-9]{12,40}$/.test(version || '') ? `&client_version=${version}` : '';
  const visibility = document.visibilityState==='hidden' ? 'hidden' : 'visible';
  return readLatestLandingJob(`${base}/history?limit=1${release}&request_purpose=landing_latest&workspace_visibility=${visibility}`, {
    signal, headers: { Authorization: `Bearer ${session.access_token}` },
    visibility: () => document.visibilityState === 'hidden' ? 'hidden' : 'visible',
    onDiagnostic: (event, values) => {
      const key = `${event}:${values.error_code || 'slow'}`;
      if (!window.instantTranscriberAcquisition?.analyticsAllowed?.()
          || typeof window.gtag !== 'function' || Date.now() - (readEventTimes.get(key) || 0) < 60000) return;
      readEventTimes.set(key, Date.now());
      window.gtag('event', event, { ...window.instantTranscriberAcquisition.context(), ...values });
    },
  });
}
